<template>
  <div class="health-form-wrapper">
		<div class="page-header">
      <h1 class="page-title">
        {{ t('healthInsurance.title') }}
      </h1>
      <p class="page-subtitle">
        {{ t('healthInsurance.subtitle') }}
      </p>
    </div>
		<form @submit.prevent="handleSubmit" class="form-card">
      <div class="space-y-8">
        <div class="container_wrap">
					<PersonalDetails v-model="formData.personal" :formErrors="formErrors" @removeError="removeError"/>
					<SectionAGeneral v-model="formData.sectionA" :formErrors="formErrors" @removeError="removeError" @setError = "setError"/>
					<SectionBMedical v-model="formData.sectionB" :gender="formData.personal?.gender" :formErrors="formErrors" @removeError="removeError"/>
					<WaiverConsent v-model="formData.waiverAccepted" :formErrors="formErrors" @removeError="removeError"/>
					<DeclarationBlock v-model="formData.declarationAccepted" :formErrors="formErrors" @removeError="removeError"/>
					<SectionEConsent v-model="formData.consentToInformation" :formErrors="formErrors" @removeError="removeError"/>
					<SignatureBlock v-model="formData.signature" :formErrors="formErrors" @removeError="removeError"/>
				</div>
			</div>
			<div class="form-actions">
        <router-link 
          class="text-decoration-underline"
          :to="{name:'EmployerFormPage'}">
          {{ t('common.previousPage') }}
        </router-link>
        <button type="submit" class="btn-primary">
          {{ t('common.continue') }}
        </button>
			</div>
		</form>
  </div>
</template>

<script setup>
import PersonalDetails from '@/components/healthInsurance/PersonalDetails.vue';
import SectionAGeneral from '@/components/healthInsurance/SectionAGeneral.vue';
import SectionBMedical from '@/components/healthInsurance/SectionBMedical.vue';
import WaiverConsent from '@/components/healthInsurance/WaiverConsent.vue';
import DeclarationBlock from '@/components/healthInsurance/DeclarationBlock.vue';
import SignatureBlock from '@/components/healthInsurance/SignatureBlock.vue';
import SectionEConsent from '@/components/healthInsurance/SectionEConsent.vue';
import { nextTick, onMounted, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { merge } from 'lodash';
import { http } from '@/services/http';
import Error from '@/utils/Error';
import { useToast } from 'vue-toastification';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
const toast = useToast();
const router = useRouter();
const { t } = useI18n()
const store = useStore();
onMounted(async ()=>{
  await store.dispatch('setLocale','en');
	await fetchInsuranceForm();
});
const fetchInsuranceForm = async () => {
	try {
		const { data } = await http.get('fetch-insurance-data')
		if (data) {
      if(data?.current_step == "employer_form"){
        return router.replace({ name: 'EmployerFormPage' });
      }
      if(data?.current_step == "completed"){
        return router.replace({ name: 'ThankYouPage' });
      }
      const candidate = data.params?.employer_form?.candidate || {};
      merge(
        formData,
        getDefaultForm(),
        {
          personal: {
            passport: candidate?.passport,
            firstName: candidate?.firstName,
            lastName: candidate?.lastName,
            dob: candidate?.dob,
            gender: candidate?.gender,
          }
        },
        data.params?.health_questionnaire || {}
      );
		}
	} catch (error) {
		console.error('Failed to load insurance form:', error)
	}
}
const today = new Date().toISOString().split('T')[0];
const getDefaultForm = () => ({
  personal: {},
  sectionA: {},
  sectionB: {
    nervous: { hasCondition: null, conditions: [] },
    heart: { hasCondition: null, conditions: [] },
    bloodVessel: { hasCondition: null, conditions: [] },
    metabolic: { hasCondition: null, conditions: [] },
    respiratory: { hasCondition: null, conditions: [] },
    digestive: { hasCondition: null, conditions: [] },
    liver: { hasCondition: null, conditions: [] },
    hernia: { hasCondition: null, conditions: [] },
    kidneyUrinary: { hasCondition: null, conditions: [] },
    jointsBones: { hasCondition: null, conditions: [] },
    skinSex: { hasCondition: null, conditions: [] },
    malignant: { hasCondition: null },
    ent: { hasCondition: null, conditions: [] },
    genderCondition: { hasCondition: null, conditions: [] },
  },
  waiverAccepted: false,
  declarationAccepted: false,
  consentToInformation: false,
  signature: {
    date: today,
  },
});
const formData = reactive(getDefaultForm());
// The wrapper itself must be reactive: swapping $errors on a plain object never re-renders the sections
const formErrors = reactive(new Error({}));

const handleSubmit = async () => {
	try {
		await http.post('save-health-questionnaire',formData)
    toast.success("Form submitted!");
    Object.assign(formErrors, new Error({}));
    router.push({ name: 'PaymentMethodPage' });
	} catch (error) {
		if(error?.data?.errors){
      Object.assign(formErrors, new Error(error.data.errors));
      await nextTick();
      const firstError = document.querySelector('.is-invalid');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
		}else{
			toast.error("Something went wrong!");
		}
	}
};
const removeError = async(errorName)=>{
  formErrors.remove(errorName);
}
const setError = ({ key, message }) => {
  formErrors.set(key,message);
};
</script>

<style scoped>
.health-form-wrapper {
  width: 1200px;
  margin: 0 auto;
}

/* Header */
.page-header {
  margin-bottom: 24px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #111827;
}

.page-subtitle {
  font-size: 14px;
  color: #6b7280;
  margin-top: 4px;
}

/* Card */
.form-card {
  background: #fff;
  border-radius: 14px;
  padding: 32px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.05);
  width: 100%;
  box-sizing: border-box; /* Important! ensures padding included in width */
}

/* Section spacing */
.form-card > * + * {
  margin-top: 32px;
}

.form-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 32px;
}
.btn-primary {
  background: #2563eb;
  color: #fff;
  padding: 12px 24px;
  border-radius: 10px;
}
</style>